import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions, GridRow, RowDataChangeEvent } from '@app/core/components/grid/grid.types';
import { InputComponent } from '@app/core/components/input/input.component';
import { SearchComponent } from '@app/core/components/search/search.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { LogisticService, ProductGroupItem } from '@app/services/logistic.service';
import { ProductionRoutingItem, ProductionRoutingService, ProductionRoutingWorkItem } from '@app/services/production-routing.service';
import { debounceTime, Subject, switchMap, from, catchError, EMPTY, groupBy, mergeMap, takeUntil, Observable, concatMap } from 'rxjs';
import { CertificateRequirementItem, MaterialGradeItem, OrderlineCalcFormGet, OrderlineCalcFormParams, OrderlineCalcFormService, UltraSonicClassItem, UpdateOrderlineCalcFormParams } from './orderline-calc-form.service';
import { ToastService } from '@app/core/services/toast.service';
import { GridMessagesModalComponent, GridMessagesModalData } from '@app/core/components/grid/modals/grid-messages-modal/grid-messages-modal.component';
import { GridMessage, GridMessageQueueItem, GridMessageType } from '@app/core/components/grid/grid.service';
import { StateService } from '@app/core/services/state.service';
import { MatDialog } from '@angular/material/dialog';
import { UtilityService } from '@app/core/services/utility.service';

@GridModalComponent('orderlinecalcform')
@Component({
    selector: 'tt-orderline-calc-form',
    templateUrl: './orderline-calc-form.component.html',
    styleUrls: ['./orderline-calc-form.component.css'],
})
export class OrderlineCalcFormComponent extends ComponentBaseComponent implements OnChanges, OnDestroy {
    @Input()
    public params?: OrderlineCalcFormParams;

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse>();

    public get?: OrderlineCalcFormGet;

    public grid?: GridOptions;

    public materialGrades: MaterialGradeItem[] = [];

    public certificateRequirements: CertificateRequirementItem[] = [];

    public ultraSonicClasses: UltraSonicClassItem[] = [];

    public productionRoutings: ProductionRoutingItem[] = [];

    public productionRoutingWorks: ProductionRoutingWorkItem[] = [];

    public productGroups: ProductGroupItem[] = [];

    @ViewChild('certificateTextInput')
    public certificateTextInput?: InputComponent<string>;

    @ViewChild('certificateSearchInput')
    public certificateSearchInput?: SearchComponent<string>;

    @ViewChild('ultraSonicTextInput')
    public ultraSonicTextInput?: InputComponent<string>;

    @ViewChild('ultraSonicSearchInput')
    public ultraSonicSearchInput?: SearchComponent<string>;

    private readGrid$ = new Subject<void>();

    private change$ = new Subject<UpdateOrderlineCalcFormParams>();

    private destroy$ = new Subject<void>();

    public saving = false;

    constructor(
        public orderlineCalcForm: OrderlineCalcFormService,
        public remember: RememberService,
        private logistic: LogisticService,
        private productionRouting: ProductionRoutingService,
        private modal: ModalService,
        private toast: ToastService,
        private state: StateService,
        private dialog: MatDialog,
        private utility: UtilityService,
        @Inject(TT_STATE_DIALOG) @Optional() private stateDialog: StateDialogContext,
    ) {
        super();
        this.initFromDialog(stateDialog);

        this.readGrid$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(500),
                switchMap(() => from(this.grid!.gridfunc!.read(false)).pipe(catchError(() => EMPTY))),
            )
            .subscribe();

        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.orderlineCalcForm.changesOrderlineCalcForm(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onUpdateOrderlineCalcFormResponse });

        this.gridMessages$.pipe(concatMap((message) => this.openGridMessage(message).pipe(catchError(() => EMPTY)))).subscribe();
    }

    private onUpdateOrderlineCalcFormResponse = async (response: Partial<OrderlineCalcFormGet>) => {
        if (!!this.get) {
            for (let key in response) {
                this.get[key as keyof OrderlineCalcFormGet] = response[key as keyof OrderlineCalcFormGet] as any;
            }

            const { errorcode, errormessage } = await this.orderlineCalcForm.updateOrderlineCalcForm(this.get);

            if ('' + errorcode !== '0') {
                this.toast.addToast({ id: 'error.orderlinecalcform.update', title: errormessage, translateTitle: false, type: 'danger' });
            }

            if ('weight_workpiece_kg' in response) {
                this.grid?.gridfunc?.read(false);
            }
        }
    };

    public async onMaterialGradeSelected(event?: MaterialGradeItem) {
        if (!this.get) return;

        this.get.materialgrade_keyno = event?.materialgrade_keyno ?? '0';
        this.get.materialgrade_name = event?.materialgrade_name ?? '';

        this.onChange('materialgrade_keyno');

        await this.grid?.gridfunc?.read(false);
    }

    public onWeightChanged() {
        if (!this.get) return;

        this.readGrid$.next();

        this.onChange('weight_workpiece_kg');
    }

    public onNetWeightChanged() {
        if (!this.get) return;

        this.readGrid$.next();

        this.onChange('weight_net_kg');
    }

    public onCertificateRequirementSelected(event?: CertificateRequirementItem) {
        if (!this.get) return;

        this.get.certificaterequirement_keyno = event?.certificaterequirement_keyno ?? '0';
        this.get.certificaterequirement_name = event?.certificaterequirement_name ?? '';

        this.onChange('certificaterequirement_keyno');

        setTimeout(() => this.toggleFocusForChangeOnConnectedSearchAndInputField(this.certificateTextInput, this.certificateSearchInput));
    }

    public onCertificateRequirementChanged(event: string) {
        if (event?.trim() === '') {
            this.onCertificateRequirementSelected();
        }
    }

    public onUltraSonicClassSelected(event?: UltraSonicClassItem) {
        if (!this.get) return;

        this.get.ultrasonicclass_keyno = event?.ultrasonicclass_keyno ?? '0';
        this.get.ultrasonicclass_name = event?.ultrasonicclass_name ?? '';

        this.onChange('ultrasonicclass_keyno');

        setTimeout(() => this.toggleFocusForChangeOnConnectedSearchAndInputField(this.ultraSonicTextInput, this.ultraSonicSearchInput));
    }

    public onUltraSonicClassChanged(event: string) {
        if (event?.trim() === '') {
            this.onUltraSonicClassSelected();
        }
    }

    public onProductGroupSelected(event?: ProductGroupItem) {
        if (!this.get) return;

        this.get.prodgrp_id = event?.prodgrp_id ?? '';
        this.get.prodgrp_name = event?.prodgrp_name ?? '';

        this.onChange('prodgrp_id');
    }

    public onWorkpieceThicknessMMChanged() {
        this.readGrid$.next();
        this.onChange('workpiece_thickness_mm');
    }

    public onOrderedQuantityChanged() {
        if (!this.get) return;

        this.readGrid$.next();

        this.get.amount_locval_total = +this.get.ordered_quantity * +this.get.sales_price_locval_pr_sku;

        this.onChange('ordered_quantity');
    }

    public onProductionRoutingSelected(event?: ProductionRoutingItem) {
        if (!this.get) return;

        this.get.productionrouting_keyno = event?.productionrouting_keyno ?? '0';
        this.get.productionrouting_name = event?.productionroutinglang_name ?? '';

        this.remember.remember('w_orderlinecalcform.productionrouting_keyno', this.get.productionrouting_keyno);

        if (!!event) {
            this.loadProductionWork();
        } else {
            this.productionRoutingWorks = [];
        }

        this.grid?.gridfunc?.read(false);
        this.onChange('productionrouting_keyno');
    }

    public async onProductionRoutingWorkSelectedChange(event: { [key: string]: any }[]) {
        if (!this.get) return;

        this.get!.options_selected = this.productionRoutingWorks
            .filter((item) => [true, 'true', 1, '1'].includes(item.is_selected))
            .reduce((acc, item) => `${acc}, ${item.operation_id}`, '')
            .substring(2);

        this.onChange('options_selected');
        await this.grid?.gridfunc?.read();
    }

    public setCostAmountLocval() {
        const amount = this.grid?.gridfunc?.getGridApi().getPinnedBottomRow(0)?.data['cost_amount_locval_pr_sku'] ?? 0;

        if (!!this.get && +this.get!.cost_amount_locval_pr_sku !== +amount && !isNaN(amount)) {
            this.get.cost_amount_locval_pr_sku = amount;
            this.onChange('cost_amount_locval_pr_sku');
        }
    }

    public onRowDataChange(event: RowDataChangeEvent) {
        if (event.saved) this.setCostAmountLocval();
    }

    public async onChange(key: keyof OrderlineCalcFormGet) {
        if (!this.get) return;

        this.change$.next({ ...this.get, colname_changed: key });
    }

    public async openMaterialGrades() {
        if (!this.get) return;

        const response: GridRow | undefined = await this.modal.openStateDialog({ state: 'materialgrades' });

        if (!!response && 'materialgrade_keyno' in response && 'materialgrade_name' in response) {
            this.onMaterialGradeSelected(response as MaterialGradeItem);
        }
    }

    public async openCertificateRequirements() {
        if (!this.get) return;

        const response: GridRow | undefined = await this.modal.openStateDialog({ state: 'certificaterequirements' });

        console.log(response);
        if (!!response && 'certificaterequirement_keyno' in response && 'certificaterequirement_name' in response) {
            this.onCertificateRequirementSelected(response as CertificateRequirementItem);
        }
    }

    public async openUltraSonicClasses() {
        if (!this.get) return;

        const response: GridRow | undefined = await this.modal.openStateDialog({ state: 'ultrasonicclasses' });

        if (!!response && 'ultrasonicclass_keyno' in response && 'ultrasonicclass_name' in response) {
            this.onUltraSonicClassSelected(response as UltraSonicClassItem);
        }
    }

    public async openCopyFrom() {
        if (!this.get) return;

        const response: ErrorMessageResponse = await this.modal.openStateDialog({ state: 'calccopyfrom', params: this.get.calc_keyno });

        if ('' + response.errorcode === '0') {
            await this.loadGet();
            this.loadProductionWork();
            this.grid?.gridfunc?.read(false);
        }
    }

    public async reset() {
        if (!this.get) return;

        await this.orderlineCalcForm.resetOrderlineCalcForm(this.get);

        await this.loadGet();
        this.grid?.gridfunc?.read(false);
        this.loadProductionWork();
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage, messages } = await this.orderlineCalcForm.saveOrderlineCalcForm(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ errorcode: errorcode, errormessage: errormessage });
                this.addGridMessage(messages);
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private createGrid() {
        if (!this.get) return;
        this.grid = new Grid().setRememberId('w_orderlinecalcform.grid').setLoadDataFunction({ method: 3878, parameters: () => this.get });
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.orderlineCalcForm.getOrderlineCalcForm(this.params);
            this.stateDialog?.setHeading(this.get.webpage_heading);
        }
    }

    private async loadMaterialGrades() {
        this.materialGrades = await this.orderlineCalcForm.searchMaterialGrades({ searchtext: '' });
    }

    private async loadCertificateRequirements() {
        this.certificateRequirements = await this.orderlineCalcForm.searchCertificateRequirements({ searchtext: '' });
    }

    private async loadUltraSonicClasses() {
        this.ultraSonicClasses = await this.orderlineCalcForm.searchUltraSonicClasses({ searchtext: '' });
    }

    private async loadProductionRoutings() {
        this.productionRoutings = await this.productionRouting.getProductionRoutings();
    }

    private async loadProductionWork() {
        if (!this.get) return;

        const selectedIds = this.get.options_selected.split(', ');
        const productionRoutingWorks = await this.productionRouting.getProductionRoutingWorks(this.get);

        this.productionRoutingWorks = productionRoutingWorks.map((item: ProductionRoutingWorkItem) => ({ ...item, is_selected: ['1', 1, true, 'true'].includes(item.is_selected) || selectedIds.includes(item.operation_id) }));
    }

    private async loadProductGroups() {
        this.productGroups = await this.logistic.getProductGroups();
    }

    private async init() {
        this.loadMaterialGrades();
        this.loadCertificateRequirements();
        this.loadUltraSonicClasses();
        this.loadProductionRoutings();
        this.loadProductGroups();
        await this.loadGet();
        this.loadProductionWork();
        this.createGrid();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    private async initFromDialog(stateDialog?: StateDialogContext) {
        if (!!stateDialog?.params && 'order_internal_no' in stateDialog.params) {
            stateDialog.dialogRef.disableClose = true;
            this.params = stateDialog.params as unknown as OrderlineCalcFormParams;
            stateDialog.setSize({ height: '95vh', width: '80vw' });
            await this.init();
            stateDialog.setHeading(this.get?.webpage_heading ?? '');
            this.submit.subscribe({ next: (value: ErrorMessageResponse) => stateDialog.close(value) });
        }
    }

    private toggleFocusForChangeOnConnectedSearchAndInputField = (textInput?: InputComponent<string>, searchInput?: SearchComponent<string>) => {
        textInput?.inputRef?.nativeElement.focus();

        if (!!searchInput) {
            setTimeout(() => {
                searchInput?.inputRef?.nativeElement.focus();
            }, 400);
        }
    };

    private gridMessages$ = new Subject<GridMessageQueueItem>();

    private addGridMessage(messages: GridMessage[]): void {
        const queuedMessages: GridMessageQueueItem[] = [];
        const modalGroups = new Map<GridMessageType, GridMessage[]>();

        for (const message of messages) {
            if (this.getGridMessageDisplayType(message) === 'modal') {
                const type = this.getGridMessageType(message);
                let group = modalGroups.get(type);

                if (!group) {
                    group = [];
                    modalGroups.set(type, group);
                    queuedMessages.push(group);
                }

                group.push(message);
            } else {
                queuedMessages.push(message);
            }
        }

        queuedMessages.forEach((message) => this.gridMessages$.next(message));
    }

    private openGridMessage(message: GridMessageQueueItem): Observable<unknown> {
        if (Array.isArray(message)) {
            return this.openGridMessagesModal(message);
        }

        if (this.getGridMessageDisplayType(message) === 'toast') {
            return from(
                this.toast.addToast({
                    id: message.id,
                    title: message.title,
                    translateTitle: false,
                    message: message.message,
                    translateMessage: false,
                    hideDelay: message.duration,
                    type: message.type ?? 'warning',
                    onClick: !!message.item_state
                        ? ({ toast, event }) => {
                              this.toast.animateHide(toast, 1000, 'running');

                              if (!message.item_state) return;

                              if (this.utility.isModifierKeyPressed(event)) {
                                  this.state.newTab(message.item_state, message.item_parms);
                              } else {
                                  this.state.go(message.item_state, message.item_parms);
                              }
                          }
                        : undefined,
                }),
            );
        }

        if (this.getGridMessageDisplayType(message) === 'modal') {
            return this.openGridMessagesModal([message]);
        }

        return EMPTY;
    }

    private openGridMessagesModal(messages: GridMessage[]): Observable<unknown> {
        if (messages.length < 1) return EMPTY;

        const type = this.getGridMessageType(messages[0]);
        const title = messages.find((message) => !!message.title?.trim?.())?.title ?? ' ';

        return this.dialog
            .open<GridMessagesModalComponent, GridMessagesModalData>(GridMessagesModalComponent, {
                width: '500px',
                maxWidth: '90vw',
                data: {
                    type,
                    title,
                    messages,
                },
            })
            .afterClosed();
    }

    private getGridMessageDisplayType(message: GridMessage): string {
        return `${message.display_type || 'toast'}`.trim().toLowerCase();
    }

    private getGridMessageType(message: GridMessage): GridMessageType {
        return message.type ?? 'warning';
    }
}
